import { Book } from "@/types/book";
import { Bundle } from "@/types/bundle";
import { Button } from "@/components/ui/button";
import { NetflixBookSkeleton } from "@/components/skeletons/NetflixBookSkeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Ticket, ChevronLeft, ChevronRight } from "lucide-react";
import { CountdownTimer } from "@/components/CountdownTimer";
import { SharedBookCard } from "@/components/SharedBookCard";
import { PurchaseConfirmationModal } from "@/components/PurchaseConfirmationModal";
import { CinemaComingSoonModal } from "@/components/CinemaComingSoonModal";
import { useState, useRef } from "react";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useAppConfig } from "@/hooks/use-app-config";

interface BundleShowcaseProps {
  bundle: Bundle;
  books: Book[];
  hasAccess: boolean;
  isLoading?: boolean;
  onBookClick?: (book: Book) => void;
  earlyAccessBookId?: number | null;
  isUnlocked?: boolean;
  onPurchase?: () => void;
}

export function BundleShowcase({
  bundle,
  books,
  hasAccess,
  isLoading,
  onBookClick,
  earlyAccessBookId,
  isUnlocked = false,
  onPurchase
}: BundleShowcaseProps) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showCinemaModal, setShowCinemaModal] = useState(false);
  const [originPosition, setOriginPosition] = useState<{ x: number; y: number } | undefined>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { openAuthModal } = useAuthModal();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { data: appConfig } = useAppConfig();

  // Calculate pricing display
  const bundlePrice = bundle.price || 0;
  const individualPrice = books.reduce((sum, book) => sum + (book.price || 0), 0);

  const isEarlyAccess = bundle.bundle_type === 'temporal' && earlyAccessBookId && !isUnlocked;
  const isSubModel = appConfig?.current_model === 'subscription';

  // Determine time-based states
  const now = new Date();
  const bundleEndDate = bundle.end_date ? new Date(bundle.end_date) : null;
  const isExpired = bundleEndDate ? now > bundleEndDate : false;
  
  // Calculate time until unlock for early access
  let timeUntilUnlock = null;
  if (!isUnlocked && bundle.start_date) {
    const startDate = new Date(bundle.start_date);
    if (now < startDate) {
      const diff = startDate.getTime() - now.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      timeUntilUnlock = { days, hours };
    }
  }

  // Filter books based on access
  let bundleBooks = books;
  
  // Get purchase content based on access state
  const getPurchaseContent = () => {
    if (isEarlyAccess && !isSubModel) {
      return {
        buttonText: 'Early Access Bundle'
      };
    }
    
    return {
      buttonText: 'Get Bundle'
    };
  };

  const purchaseContent = getPurchaseContent();

  const handleBookClick = async (book: Book) => {
    // If this is an upcoming bundle without access, show cinema modal
    if (bundle.status === 'upcoming' && !hasAccess) {
      // Set a default center position for the animation
      setOriginPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });
      setShowCinemaModal(true);
      return;
    }

    // If this is an upcoming bundle with access but not unlocked yet, allow normal flow
    if (bundle.status === 'upcoming' && hasAccess && !isUnlocked) {
      // Allow early access book selection or reading
    }

    if (onBookClick) {
      onBookClick(book);
      return;
    }

    // Check authentication first
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.log('User not authenticated - opening auth modal');
      openAuthModal();
      return;
    }

    // Handle book selection logic here
    console.log('Book clicked:', book.title);
  };

  const handlePurchaseClick = () => {
    if (onPurchase) {
      onPurchase();
    }
  };

  const handleModalPurchase = () => {
    setShowPurchaseModal(false);
    if (onPurchase) {
      onPurchase();
    }
  };

  return (
    <>
      <div className={`space-y-1 py-0 ${bundle.bundle_type === 'permanent' ? 'mb-4' : 'mb-1'}`}>
        {/* Header section - only show for temporal bundles */}
        {bundle.bundle_type === 'temporal' && (
          <div className="px-5 md:px-12 xl:px-16">
            <div className="flex items-center justify-between mb-2 py-[2px]">
              <div 
                className={bundle.status === 'upcoming' && !hasAccess ? "cursor-pointer" : ""}
                onClick={() => {
                  if (bundle.status === 'upcoming' && !hasAccess) {
                    setShowCinemaModal(true);
                  }
                }}
              >
                <div>
                  <h2 className="text-2xl md:text-4xl xl:text-5xl font-display font-bold text-white mb-0 py-0 my-0 leading-tight">
                    {bundle.title}
                  </h2>
                  
                  {bundle.status === 'active' && (
                    <div className="text-xs md:text-sm uppercase tracking-wider text-emerald-400 font-semibold mb-1 md:mb-2 mt-1 md:mt-2">
                      NOW SHOWING
                    </div>
                  )}
                  {bundle.status === 'upcoming' && (
                    <div className="text-xs md:text-sm uppercase tracking-wider text-amber-400 font-semibold mb-1 md:mb-2 mt-1 md:mt-2">
                      COMING SOON
                    </div>
                  )}
                </div>
              </div>
              
              {/* Glass Navigation Buttons - Desktop Only */}
              <div className="hidden md:flex gap-3">
                <button
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
                    }
                  }}
                  className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 hover:scale-105"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                    }
                  }}
                  className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 hover:scale-105"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {!isUnlocked && timeUntilUnlock && (
              <div className="mb-4 md:mb-6">
                <div className="text-white/80 text-sm md:text-base mb-2">
                  Unlocks in:
                </div>
                <CountdownTimer targetDate={new Date(bundle.start_date!)} />
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 md:gap-8 mt-4 md:mt-6">
              <div className="flex-1">
                {/* Early Access Notification */}
                {earlyAccessBookId && !isUnlocked && (
                  <div className="text-emerald-400 text-sm md:text-base mt-2 flex items-center gap-2">
                    <span>✨ Early access book selected! You can now read it.</span>
                  </div>
                )}
              </div>

              {/* Purchase button for temporal bundles - hidden in subscription model */}
              {!isSubModel && !hasAccess && !isExpired && onPurchase && (
                <div className="flex flex-col justify-end h-full">
                  <button
                    onClick={handlePurchaseClick}
                    className="text-xs md:text-sm py-2 md:py-3 px-4 md:px-6 h-auto max-h-[48px] md:max-h-[56px]
                              backdrop-blur-md border-2 border-purple-500 rounded-lg
                              text-white transition-all duration-300 ease-in-out cursor-pointer
                              bg-black/30 shadow-[0_0_20px_rgba(147,51,234,0.6)]
                              flex items-center justify-center whitespace-nowrap"
                    style={{
                      boxShadow: '0 0 30px rgba(147, 51, 234, 0.8), inset 0 0 30px rgba(147, 51, 234, 0.1)',
                    }}
                  >
                    <div className="relative z-10 flex items-center justify-center gap-1 md:gap-2">
                      <Ticket className="w-3 h-3 md:w-4 md:h-4" />
                      {purchaseContent.buttonText}
                    </div>
                  </button>
                </div>
              )}
              
            </div>
          </div>
        )}

        {/* Permanent bundle title - simple layout */}
        {bundle.bundle_type === 'permanent' && (
          <div className="px-5 md:px-12 xl:px-16 mb-4 md:mb-6">
            <h2 className="text-2xl md:text-4xl xl:text-5xl font-display font-bold text-white mb-2">
              {bundle.title}
            </h2>
          </div>
        )}

        {/* Books carousel section */}
        <div className="relative" style={{
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)'
        }}>
          <ScrollArea className="w-full">
            <div ref={scrollContainerRef} className="overflow-x-auto no-scrollbar bundle-books-scroll">
              <div className="flex space-x-2 md:space-x-3 xl:space-x-4 pb-4 md:pb-6 pt-2 pl-5 md:pl-12 xl:pl-16 no-scrollbar" style={{
                paddingRight: '32px', // Extra breathing room at end of carousel
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                {isLoading ? (
                  Array(6).fill(0).map((_, index) => (
                    <div key={`skeleton-${index}`} className="w-[140px] md:w-[280px] xl:w-[320px] flex-shrink-0">
                      <NetflixBookSkeleton delay={index * 100} />
                    </div>
                  ))
                ) : (
                  bundleBooks.map(book => (
                    <div key={book.id} className="w-[140px] md:w-[280px] xl:w-[280px] flex-shrink-0">
                      <SharedBookCard 
                        title={book.title} 
                        cover_image_url={book.cover_image_url || ''} 
                        rating={book.rating} 
                        year={book.year?.toString()} 
                        bookId={book.id}
                        cacheVersion={book.cache_version}
                        onClick={() => handleBookClick(book)} 
                        hideRatings={true}
                        bundleStartDate={bundle.start_date}
                        bundleTagType={bundle.status === 'active' ? 'current' : bundle.status === 'upcoming' ? 'upcoming' : bundle.status === 'expired' ? 'expired' : undefined}
                        bundleId={bundle.id}
                        showNotifyMe={bundle.status === 'upcoming' && !hasAccess}
                      />
                    </div>
                  ))
                )}
                {/* End spacer for right padding in scroll containers */}
                <div className="flex-shrink-0 w-4 md:w-8 xl:w-12" aria-hidden="true" />
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Purchase Confirmation Modal */}
      <PurchaseConfirmationModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        purchaseType={isEarlyAccess ? 'early_access' : 'bundle'}
        bundle={bundle}
        bundleBooks={bundleBooks}
        originalPrice={bundlePrice || 0}
        userCredits={0} // This should be passed from parent component
        creditsEarned={Math.floor((bundlePrice || 0) * 0.1)} // 10% back in credits
        onPurchase={handleModalPurchase}
        bundleId={bundle.id}
        earlyAccessBookId={earlyAccessBookId}
      />

      {/* Cinema Coming Soon Modal */}
      <CinemaComingSoonModal
        isOpen={showCinemaModal}
        onClose={() => setShowCinemaModal(false)}
        bundle={bundle}
        books={books}
        originPosition={originPosition}
      />
    </>
  );
}